import { useLoaderData } from "react-router";
import React from "react";
import TwoColumnLayout from "layout/TwoColumnLayout";
import { isPanelVisible } from "utilities/panelUtils";
import UserTasksPanel from "userTask/list/app";
import NotesPanel from "note/list/app";
import EmailsPanel from "email/list/App";
import DocumentFromTemplatePanel from "DocumentFromTemplate/app";
import FilesPanel from "show/files/app";
import { UploadFilesConfig, uploadFilesToCoreApi } from "utilities/fileUploadUtils";
import LinkShowApp from "document/linkshow/app";
import GeneralOptionsPanel from "components/optionspanel/GeneralOptionsPanel";
import { DeleteDialog } from "components";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import ProductsPanel, { useProductsByParentQuery } from "eSign/panelsegment/ProductsPanel";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import { getDocumentFromTemplateQueryKey } from "DocumentFromTemplate/app";
import { emailTemplateTypeForDocumentType, getDocumentRoutes, type DocumentTypeWithoutUnknown } from "utilities/documentUtils";
import { fileApis, getDocumentCoreApiExceptInvoice } from "utilities/apiUtils";
import { mapDocumentToFormState } from "./documentFormState";
import { GenericDocumentV1DetailSection } from "./GenericDocumentV1DetailSection";
import AreYouSureModal from "components/modals/AreYouSureModal";

export const DocumentV1DetailApp = () => {
  const viewModel = useLoaderData<server.viewmodel.DocumentV1DetailFrontModel>();
  const [document, setDocument] = React.useState(viewModel.document);
  const [documentFormState, setDocumentFormState] = React.useState(mapDocumentToFormState(viewModel.document));
  const [euVatNumber, setEuVatNumber] = React.useState(viewModel.euVatNumber);
  const [minSendDate, setMinSendDate] = React.useState(moment.utc(viewModel.minimumSendDate));
  const [maxSendDate, setMaxSendDate] = React.useState(moment.utc(viewModel.maximumSendDate));
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isLocking, setIsLocking] = React.useState(false);
  const [isUnlocking, setIsUnlocking] = React.useState(false);

  const documentType = document.documentType as server.dto.MainAreas;

  const documentRoutes = getDocumentRoutes(document.documentType as DocumentTypeWithoutUnknown, document.documentId);
  const documentApi = getDocumentCoreApiExceptInvoice(documentType);
  const productsByParentQuery = useProductsByParentQuery(documentType, document.documentId, viewModel.productsPanel.data);
  const queryClient = useQueryClient();

  const fileApi = fileApis[documentType];
  const exportOptionsApi = documentApi.ExportOptionsForDetail;


  const refetchDocument = async () => {
    const res = await documentApi.GetSidePanelData(document.documentId);
    setDocument(res.data.data.document);
    setDocumentFormState(mapDocumentToFormState(res.data.data.document));
    setMinSendDate(moment.utc(res.data.data.minimumSendDate));
    setMaxSendDate(moment.utc(res.data.data.maximumSendDate));
    setEuVatNumber(res.data.data.euVatNumber);
    queryClient.invalidateQueries(getDocumentFromTemplateQueryKey(document.documentId));
  };

  const onRefetchedProducts = () => {
    refetchDocument();
  };

  const onRefetchedShows = () => {
    refetchDocument();
  };

  const lock = async () => {
    const res = await window.CoreApi.Document.Lock(document.documentId)
    if (!res.data.hasErrors) {
      window.location.reload();
    }
    return res.data;
  }

  const unlock = async () => {
    const res = await window.CoreApi.Document.Unlock(document.documentId)
    if (!res.data.hasErrors) {
      window.location.reload();
    }
    return res.data;
  }


  const apis = getDocumentCoreApiExceptInvoice(documentType);

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <SectionPanel.Section>
        <GenericDocumentV1DetailSection
          document={document}
          formState={documentFormState}
          onChange={setDocumentFormState}
          products={productsByParentQuery.data}
          minSendDate={minSendDate}
          maxSendDate={maxSendDate}
          refetchDocument={refetchDocument}
          refetchProducts={productsByParentQuery.refetch}
          lastModifiedUser={viewModel.lastModifiedUser}
          artistRiders={viewModel.artistRiders}
          euVatNumber={euVatNumber}
        />
      </SectionPanel.Section>

      {isPanelVisible(viewModel.productsPanel.config) && <ProductsPanel
        useSectionPanel={false}
        documentId={document.documentId}
        documentType={document.documentType}
        productsPanel={viewModel.productsPanel}
        onRefetchedProducts={onRefetchedProducts}
      />}

      {isPanelVisible(viewModel.linkedUserTaskPanelConfig) && <UserTasksPanel
        mainArea={documentType}
        config={viewModel.linkedUserTaskPanelConfig}
      />}

      {isPanelVisible(viewModel.linkedNotePanelConfig) && <NotesPanel
        mainArea={documentType}
        config={viewModel.linkedNotePanelConfig}
      />}

      {isPanelVisible(viewModel.linkedEmailPanelConfig) && <EmailsPanel
        mainArea={documentType}
        emailTemplateType={emailTemplateTypeForDocumentType(document.documentType)}
        config={viewModel.linkedEmailPanelConfig}
      />}

      {isPanelVisible(viewModel.generateDocumentPanelConfig) && exportOptionsApi && <DocumentFromTemplatePanel
        itemId={document.documentId}
        api={exportOptionsApi}
        panelTitle={viewModel.generateDocumentPanelConfig.headerLabel}
      />}

      {isPanelVisible(viewModel.linkedFilePanelConfig) && fileApi && <FilesPanel
        config={viewModel.linkedFilePanelConfig}
        uploadFiles={(files: File[], uploadConfig: UploadFilesConfig) => {
          return uploadFilesToCoreApi(
            window.CoreApi.File.UploadClientFiles,
            window.CoreApi.File.UploadClientFiles.absoluteUrl(),
            {
              files,
              mainArea: documentType,
              parentId: document.documentId
            },
            uploadConfig
          );
        }}
        fetchFilesApi={(page, perPage) => fileApi.GetFiles({ parentId: document.documentId, page, perPage })}
      />}

      {isPanelVisible(viewModel.linkedShowPanel.config) && <LinkShowApp
        linkedShowsPanel={viewModel.linkedShowPanel}
        documentType={document.documentType}
        defaultBookkeeping={document.bookkeeping}
        onShowsChange={onRefetchedShows}
      />}
    </TwoColumnLayout.LeftColumn>
    <TwoColumnLayout.RightColumn>
      <GeneralOptionsPanel
        data={viewModel.optionsPanel}
        links={[
          { text: RESX.Buttons.ListView, visible: true, href: viewModel.listUrl },
          { text: RESX.InvoiceExport.Lock, visible: document.permissions.canLockUnlock && document.locked === "unlocked" && !document.lifeCycleInfo.isDraft, onClick: () => { setIsLocking(true); } },
          { text: RESX.InvoiceExport.Unlock, visible: document.permissions.canLockUnlock && document.locked === "locked", onClick: () => { setIsUnlocking(true); } },
          { text: RESX.Buttons.btnCopy, visible: document.permissions.canModify, href: documentRoutes.copy },
          { text: RESX.Buttons.btnDelete, visible: document.permissions.canDelete, onClick: () => { setIsDeleting(true); } },
        ]}
        shortcutOrder={["product", "task", "note", "email", "file", "show"]}
      />

      {isDeleting && <DeleteDialog
        isOpen
        handleClose={() => setIsDeleting(false)}
        onDelete={() => documentApi.Delete({ documentId: document.documentId }).then((r) => {
          if (r.data.hasErrors) {
            window.displayGenericModal(RESX.GeneralLabels.Delete, r.data.errors[0].message, "tiny", "red");
            setIsDeleting(false);
            return;
          }
          window.location.href = viewModel.listUrl;
        })}
        message={RESX.GeneralWarnings.DeleteSure}
        spinForever
      />}

      {isLocking && <AreYouSureModal
        title={RESX.InvoiceExport.Lock}
        yesButtonColor="red"
        yesButtonLabel={RESX.InvoiceExport.Lock}
        onClose={() => setIsLocking(false)}
        onYes={lock}
      />}

      {isUnlocking && <AreYouSureModal
        title={RESX.InvoiceExport.Unlock}
        yesButtonColor="red"
        yesButtonLabel={RESX.InvoiceExport.Unlock}
        onClose={() => setIsUnlocking(false)}
        onYes={unlock}
      />}

      {isDeleting && <DeleteDialog
        isOpen
        handleClose={() => setIsDeleting(false)}
        onDelete={() => apis.Delete({ documentId: document.documentId }).then((r) => {
          if (r.data.hasErrors) {
            window.displayGenericModal(RESX.GeneralLabels.Delete, r.data.errors[0].message, "tiny", "red");
            setIsDeleting(false);
            return;
          }
          window.location.href = viewModel.listUrl;
        })}
        message={RESX.GeneralWarnings.DeleteSure}
        spinForever
      />}
    </TwoColumnLayout.RightColumn>
  </TwoColumnLayout>;
};

export default DocumentV1DetailApp;
